/**
 * Share helper — wraps Web Share API + Capacitor Share into a single
 * call. Falls back to download if neither is available, or if the user
 * dismisses the share sheet.
 *
 * Native iOS (Capacitor) gives us:
 *   - The proper iOS share sheet with all installed apps (Instagram,
 *     TikTok, Messages, AirDrop, etc.)
 *   - File system access — the file lands in the user's Photos library
 *     or wherever they pick.
 *
 * Web (PWA / Safari / Chrome) uses navigator.share when available;
 * downloads otherwise.
 */

import { downloadBlob } from "../record/capture";

interface CapacitorShare {
  share(opts: {
    title?: string;
    text?: string;
    url?: string;
    files?: string[];
    dialogTitle?: string;
  }): Promise<void>;
}

interface CapacitorFilesystem {
  writeFile(opts: {
    path: string;
    data: string;
    directory: string;
    encoding?: string;
    recursive?: boolean;
  }): Promise<{ uri: string }>;
}

let nativeShare: CapacitorShare | null = null;
let nativeFs: CapacitorFilesystem | null = null;
let nativeProbed = false;

const isCapacitor = (): boolean => {
  const w = window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } };
  return !!w.Capacitor?.isNativePlatform?.();
};

async function probeNative(): Promise<void> {
  if (nativeProbed) return;
  nativeProbed = true;
  if (!isCapacitor()) return;
  try {
    const shareMod = await import(/* @vite-ignore */ "@capacitor/share");
    nativeShare = (shareMod as unknown as { Share: CapacitorShare }).Share;
  } catch {
    /* not bundled in this build */
  }
  try {
    const fsMod = await import(/* @vite-ignore */ "@capacitor/filesystem");
    nativeFs = (fsMod as unknown as { Filesystem: CapacitorFilesystem }).Filesystem;
  } catch {
    /* not bundled */
  }
}

void probeNative();

/**
 * Convert a Blob to a base64 string (without the `data:...;base64,`
 * prefix). Used to write blobs to disk via Capacitor's Filesystem,
 * since the native plugin only accepts base64-encoded data.
 */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip the data URL prefix.
      const idx = result.indexOf(",");
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/**
 * Show the share sheet (native or web) with the given file. Falls back
 * to a browser download if the user dismisses or sharing is unsupported.
 */
export async function shareFile(
  blob: Blob,
  filename: string,
  opts: { title?: string; text?: string } = {},
): Promise<void> {
  // Native (Capacitor) path — write to cache, hand the URI to the share sheet.
  if (nativeShare && nativeFs) {
    try {
      const data = await blobToBase64(blob);
      const written = await nativeFs.writeFile({
        path: filename,
        data,
        directory: "CACHE",
      });
      await nativeShare.share({
        title: opts.title ?? "PulseFX clip",
        text: opts.text ?? "Made with PulseFX",
        files: [written.uri],
        dialogTitle: "Share PulseFX clip",
      });
      return;
    } catch (err) {
      // Native share rejected (cancelled or error) — fall through to web/download.
      if (
        (err as { message?: string })?.message?.includes("cancel") ||
        (err as { code?: string })?.code === "USER_CANCELLED"
      ) {
        return; // user dismissed, that's fine
      }
    }
  }

  // Web Share API path — works on Android Chrome + iOS Safari (PWA).
  const nav = navigator as Navigator & {
    canShare?: (data: { files?: File[] }) => boolean;
    share?: (data: { files?: File[]; title?: string; text?: string }) => Promise<void>;
  };
  const file = new File([blob], filename, { type: blob.type });
  if (nav.canShare && nav.share && nav.canShare({ files: [file] })) {
    try {
      await nav.share({
        files: [file],
        title: opts.title ?? "PulseFX clip",
        text: opts.text ?? "Made with PulseFX",
      });
      return;
    } catch (err) {
      // AbortError = user dismissed; not an error.
      if ((err as { name?: string })?.name === "AbortError") return;
    }
  }

  // Final fallback: trigger a browser download.
  downloadBlob(blob, filename);
}
